import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { forkJoin, of, ReplaySubject, timer } from 'rxjs';
import { filter, finalize, first, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { DocumentsService } from '@services/documents.service';
import { CompanyModel } from '@data-layer/models/companies';
import { CompaniesService } from '@services/companies.service';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { InvoiceModel } from '@data-layer/models/entry-summaries';
import { IDefaultSelect } from '@interfaces/default-select';
import { UntypedFormControl } from '@angular/forms';
import { DEFAULT_MAX_POLLING_RETRY, DEFAULT_POLLING_TIMEOUT } from '@constants/constants';
import { enumOcrStatus } from '@data-layer/enums/common';
import { DocumentModel } from '@data-layer/models/documents';

@Component({
  selector: 'es-invoice-replace-ocr',
  templateUrl: './es-invoice-replace-ocr.component.html',
  styleUrls: ['./es-invoice-replace-ocr.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsInvoiceReplaceOcrComponent implements OnInit, OnDestroy {
  @Input() public existingInvoice: InvoiceModel;
  @Input() public billOfLadingId: number;
  @Input() public rootEntityId: number;
  @Input() public rootEntityType: enumDocumentRootEntityType;
  @Input() public isBatchES: boolean;
  @Input() public isSmallIcon: boolean;
  @Output() public fileUploaded: EventEmitter<void> = new EventEmitter<void>();
  @Output() public rerunOCR: EventEmitter<File> = new EventEmitter<File>();
  @Output() public loadingChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  public company: CompanyModel;
  public invoiceId: number;
  public isLoading: boolean;
  public isShowOCRModal: boolean;
  public uploadOptions: IDefaultSelect[];
  public uploadFc: UntypedFormControl;
  public documentUploadOcrId: string;

  private RESCAN_ID: number = 1;
  private UPLOAD_ID: number = 2;
  private file: File;

  private POLLING_TIMEOUT: number = 1500;
  private MAX_POLLING_RETRY: number = 80;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private companiesService: CompaniesService,
    private entrySummaryStateService: EntrySummaryStateService,
    private documentsService: DocumentsService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.company = this.companiesService.getCompany();

    this.uploadOptions = [
      { id: this.RESCAN_ID, label: 'OPTION_OCR_RESCAN', value: this.RESCAN_ID },
      { id: this.UPLOAD_ID, label: 'OPTION_OCR_UPLOAD', value: this.UPLOAD_ID },
    ];
    this.uploadFc = new UntypedFormControl(this.RESCAN_ID);
    this.documentUploadOcrId = 'file-input-ocr-replace-' + this.billOfLadingId;
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public showOCRModal(): void {
    this.isShowOCRModal = true;
  }

  public hideOCRModal(): void {
    this.isShowOCRModal = false;
  }

  public onUploadFile(file: File): void {
    if (!this.company) {
      return;
    }

    this.file = file;

    if (this.isBatchES) {
      this.onProcessWithoutOCRFile();
    } else {
      this.showOCRModal();
    }
  }

  public onProcessFile(): void {
    this.hideOCRModal();

    if (this.uploadFc.value === this.UPLOAD_ID) {
      this.onProcessWithoutOCRFile();
    } else {
      this.rerunOCR.emit(this.file);
    }
  }

  public onProcessWithoutOCRFile(): void {
    this.isLoading = true;
    this.loadingChanged.emit(this.isLoading);

    this.entrySummaryStateService.entryDocuments$
      .pipe(
        take(1),
        switchMap((docs: DocumentModel[]) => {
          const currentFiles: DocumentModel[] = docs.filter(
            (item: DocumentModel) =>
              item.relatedEntityId === this.existingInvoice.id &&
              [enumDocumentType.INVOICE, enumDocumentType.FILE_PREVIEW].includes(item.type),
          );

          return !!currentFiles.length
            ? forkJoin(currentFiles.map((item: DocumentModel) => this.entrySummaryStateService.removeDocument(item.id)))
            : of(currentFiles);
        }),
        switchMap(() => this.entrySummaryStateService.runDocumentRecognition(this.existingInvoice.id, false)),
        switchMap(() =>
          this.entrySummaryStateService.uploadDocumentClear(
            {
              relatedEntityId: this.existingInvoice.id,
              type: enumDocumentType.INVOICE,
              relatedEntityType: enumDocumentRelatedEntityType.INVOICE,
              rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
              rootEntityId: this.rootEntityId,
              fileName: null,
            },
            this.file,
            this.company.id,
          ),
        ),
        switchMap((uploadedDocumentId: number) => {
          return timer(0, DEFAULT_POLLING_TIMEOUT).pipe(
            take(DEFAULT_MAX_POLLING_RETRY),
            switchMap(() => this.documentsService.getDocumentState(uploadedDocumentId, false)),
            filter((status: enumDocumentState) => ![enumDocumentState.CREATED, enumDocumentState.SCAN_PENDING].includes(status)),
            first(),
          );
        }),
        switchMap(() => {
          return timer(0, this.POLLING_TIMEOUT).pipe(
            take(this.MAX_POLLING_RETRY),
            switchMap(() => this.entrySummaryStateService.getOcrStatus(this.existingInvoice.id)),
            tap((status: enumOcrStatus) => {
              if (status > enumOcrStatus.PROCESSING) {
                this.entrySummaryStateService.currentOCRState$.next(status);
              }
            }),
            filter(
              (status: enumOcrStatus) =>
                ![
                  enumOcrStatus.PROCESSING,
                  enumOcrStatus.CONVERTING_FILE_TO_IMAGE,
                  enumOcrStatus.EXTRACTING_DATA,
                  enumOcrStatus.CLASSIFYING_LINE_ITEMS,
                ].includes(status),
            ),
            first(),
          );
        }),
        switchMap(() => {
          return this.entrySummaryStateService.updateDocuments(true);
        }),
        tap(() => this.entrySummaryStateService.currentOCRState$.next(null)),
        finalize(() => {
          this.isLoading = false;
          this.loadingChanged.emit(this.isLoading);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(
        () => {
          this.fileUploaded.emit();
        },
        () => {
          this.isLoading = false;
          this.loadingChanged.emit(this.isLoading);
          this.cdr.markForCheck();
        },
      );
  }
}
